import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Brain, RefreshCw, Loader2, TrendingUp, TrendingDown, Zap, CheckCircle2,
  AlertTriangle, AlertCircle, Sparkles, Activity, Database, GitBranch, Clock,
  ChevronDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Theme tokens (dark futuristic, neon-glow accents) ─────────────────────
const C = {
  bg: "var(--bc-bg, #020202)", surface: "var(--bc-panel, #09090B)", panel: "#0F0F11", highlight: "var(--bc-panel-elev, #141416)",
  border: "var(--bc-border, rgba(255,255,255,0.07))", text: "var(--bc-ink, #FFFFFF)", muted: "var(--bc-sub, #71717A)",
  green: "#22C55E", red: "#EF4444", cyan: "#06B6D4",
  amber: "#F59E0B", purple: "#8B5CF6",
};
const NEON = "#22FFA3";

// ── Market label dictionary (kebab + human-friendly) ──────────────────────
const MARKET_LABEL = {
  home: "Vittoria Casa (1)", draw: "Pareggio (X)", away: "Vittoria Trasferta (2)",
  over15: "Over 1.5 Goal", over25: "Over 2.5 Goal", over35: "Over 3.5 Goal",
  under15: "Under 1.5 Goal", under25: "Under 2.5 Goal", under35: "Under 3.5 Goal",
  btts_yes: "Gol/Gol", btts_no: "No Gol",
  double_1x: "Doppia 1X", double_x2: "Doppia X2", double_12: "Doppia 12",
  // Team multigol
  home_0_1: "Multigol Casa 0-1", home_0_2: "Multigol Casa 0-2",
  home_1_2: "Multigol Casa 1-2", home_1_3: "Multigol Casa 1-3",
  home_2_3: "Multigol Casa 2-3", home_2_4: "Multigol Casa 2-4",
  home_2_5: "Multigol Casa 2-5",
  away_0_1: "Multigol Trasferta 0-1", away_0_2: "Multigol Trasferta 0-2",
  away_1_2: "Multigol Trasferta 1-2", away_1_3: "Multigol Trasferta 1-3",
  away_2_3: "Multigol Trasferta 2-3", away_2_4: "Multigol Trasferta 2-4",
  away_2_5: "Multigol Trasferta 2-5",
  // Half goals
  first_half_over_05: "Over 0.5 1°T", first_half_over_15: "Over 1.5 1°T",
  first_half_btts: "Gol/Gol 1°T",
  second_half_over_05: "Over 0.5 2°T", second_half_over_15: "Over 1.5 2°T",
  second_half_btts: "Gol/Gol 2°T",
};
const labelOf = (k) => MARKET_LABEL[k] || k;

const SCENARIO_LABEL = {
  "xg_low|skew_home":     "match prudenti con casa favorita",
  "xg_low|skew_away":     "match prudenti con trasferta favorita",
  "xg_low|skew_balanced": "match prudenti equilibrati",
  "xg_mid|skew_home":     "match medi con casa favorita",
  "xg_mid|skew_away":     "match medi con trasferta favorita",
  "xg_mid|skew_balanced": "match medi equilibrati",
  "xg_high|skew_home":    "match offensivi con casa favorita",
  "xg_high|skew_away":    "match offensivi con trasferta favorita",
  "xg_high|skew_balanced":"match offensivi equilibrati",
};

// ── Helpers ───────────────────────────────────────────────────────────────
const fmtNumber = (v, d = 2) => (v == null || Number.isNaN(v)) ? "—" : Number(v).toFixed(d);

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return "ora";
    if (sec < 3600) return `${Math.floor(sec / 60)} min fa`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h fa`;
    return `${Math.floor(sec / 86400)} giorni fa`;
  } catch { return iso; }
}

// LogLoss → "accuracy bonus" so users see a positive number rising.
const accuracyBonus = (before, after) => {
  if (!before || !after || before <= 0) return 0;
  return ((before - after) / before) * 100;
};

/* ─── Auto-derived insights from calibration data ─────────────────────── */
function deriveInsights(state) {
  if (!state || !state.applied) return { strengths: [], weaknesses: [], scenarios: [] };

  // Combine global (1X2/OU/BTTS) + team_markets (multigol/HT/ST) calibrators.
  const allCals = {
    ...((state.calibrators && state.calibrators.global) || {}),
    ...((state.team_markets && state.team_markets.global) || {}),
  };
  const cards = Object.entries(allCals)
    .filter(([, v]) => v && v.n >= 100 && v.logloss_after != null)
    .map(([k, v]) => ({
      key: k,
      label: labelOf(k),
      n: v.n,
      bias_pp: ((v.b ?? 0) * 100), // approximate bias in pp
      improvement_pct: accuracyBonus(v.logloss_before, v.logloss_after),
      ll_after: v.logloss_after,
      ll_before: v.logloss_before,
    }));

  // Strengths: biggest accuracy gain after calibration on >100 samples.
  const strengths = [...cards]
    .sort((a, b) => b.improvement_pct - a.improvement_pct)
    .slice(0, 3);

  // Weaknesses: highest residual logloss (model still struggles even after cal).
  const weaknesses = [...cards]
    .sort((a, b) => b.ll_after - a.ll_after)
    .slice(0, 3);

  // Scenarios: from error_patterns (xg × skew buckets) — biggest |bias_pp|.
  const ep = state.error_patterns || {};
  const scenarios = [];
  Object.entries(ep).forEach(([market, buckets]) => {
    Object.entries(buckets || {}).forEach(([scenarioKey, info]) => {
      if (!info || (info.n || 0) < 50) return;
      scenarios.push({
        market: labelOf(market),
        scenarioKey,
        scenarioLabel: SCENARIO_LABEL[scenarioKey] || scenarioKey,
        bias_pp: info.bias_pp,
        n: info.n,
      });
    });
  });
  scenarios.sort((a, b) => Math.abs(b.bias_pp) - Math.abs(a.bias_pp));
  return { strengths, weaknesses, scenarios: scenarios.slice(0, 5) };
}

/* ─── Sub-components ──────────────────────────────────────────────────── */

function StatCard({ icon: Icon, label, value, sublabel, color = NEON, testid }) {
  return (
    <div data-testid={testid}
      style={{
        background: C.surface, border: `1px solid ${color}33`,
        padding: "1.25rem 1rem", position: "relative", overflow: "hidden",
        boxShadow: `0 0 20px ${color}11, inset 0 0 20px ${color}05`,
      }}>
      <div style={{ position: "absolute", top: 0, left: 0, height: "2px", width: "100%",
        background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />
      <div style={{ display: "flex", alignItems: "center", gap: ".55rem", marginBottom: ".5rem" }}>
        <Icon size={13} style={{ color, filter: `drop-shadow(0 0 4px ${color})` }} />
        <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".7rem",
          color: C.muted, textTransform: "uppercase", letterSpacing: ".18em" }}>
          {label}
        </span>
      </div>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "1.85rem",
        color: C.text, fontWeight: 600, lineHeight: 1.1, letterSpacing: "-.01em" }}>
        {value}
      </div>
      {sublabel && (
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: ".72rem",
          color: C.muted, marginTop: ".25rem" }}>
          {sublabel}
        </div>
      )}
    </div>
  );
}

function NeonBar({ value, max = 100, color = NEON, height = 6 }) {
  const pct = Math.max(0, Math.min(100, (Math.abs(value) / max) * 100));
  return (
    <div style={{ background: C.highlight, height, position: "relative", overflow: "hidden",
      borderRadius: 1 }}>
      <div style={{
        height: "100%", width: `${pct}%`,
        background: `linear-gradient(90deg, ${color}88, ${color})`,
        boxShadow: `0 0 10px ${color}, inset 0 0 4px ${color}66`,
        transition: "width .9s cubic-bezier(.2,.7,.2,1)",
      }} />
    </div>
  );
}

function StrengthRow({ item, rank }) {
  const color = NEON;
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "30px 1fr 100px 90px",
      alignItems: "center", gap: "1rem", padding: ".75rem 0",
      borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".85rem",
        color, fontWeight: 700, letterSpacing: ".15em" }}>#{rank}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: ".82rem",
          color: C.text, fontWeight: 600 }}>{item.label}</div>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: ".62rem",
          color: C.muted, marginTop: ".1rem" }}>
          su {item.n.toLocaleString("it-IT")} match
        </div>
      </div>
      <NeonBar value={item.improvement_pct} max={3} color={color} />
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: ".82rem",
        color, fontWeight: 600, textAlign: "right",
        textShadow: `0 0 8px ${color}55` }}>
        +{fmtNumber(item.improvement_pct)}%
      </div>
    </div>
  );
}

function WeaknessRow({ item, rank }) {
  const isHard = item.ll_after >= 0.65;
  const color = isHard ? C.amber : C.cyan;
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "30px 1fr 100px 90px",
      alignItems: "center", gap: "1rem", padding: ".75rem 0",
      borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".85rem",
        color, fontWeight: 700, letterSpacing: ".15em" }}>#{rank}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: ".82rem",
          color: C.text, fontWeight: 600 }}>{item.label}</div>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: ".62rem",
          color: C.muted, marginTop: ".1rem" }}>
          su {item.n.toLocaleString("it-IT")} match · LogLoss {fmtNumber(item.ll_after, 3)}
        </div>
      </div>
      <NeonBar value={item.ll_after} max={1} color={color} />
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: ".75rem",
        color, fontWeight: 600, textAlign: "right" }}>
        {isHard ? "DIFFICILE" : "MEDIA"}
      </div>
    </div>
  );
}

function ScenarioWarning({ scenario }) {
  const isOver = scenario.bias_pp > 0;
  // bias_pp positive → model UNDER-estimated this market in this scenario,
  // i.e. real outcomes happen MORE than predicted.
  const verb = isOver ? "si verifica più spesso" : "si verifica meno spesso";
  const color = isOver ? NEON : C.amber;
  const Icon = isOver ? TrendingUp : TrendingDown;
  return (
    <div style={{
      background: `${color}08`, border: `1px solid ${color}33`,
      padding: "0.75rem 0.9rem", display: "flex", gap: ".65rem", alignItems: "flex-start",
    }}>
      <Icon size={14} style={{ color, flexShrink: 0, marginTop: "1px",
        filter: `drop-shadow(0 0 4px ${color}88)` }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: ".82rem",
          color: C.text, lineHeight: 1.45 }}>
          <strong style={{ color }}>{scenario.market}</strong> {verb} del previsto nei{" "}
          <span style={{ color: C.text, fontWeight: 600 }}>{scenario.scenarioLabel}</span>.
        </div>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: ".66rem",
          color: C.muted, marginTop: ".2rem" }}>
          Scostamento: {scenario.bias_pp > 0 ? "+" : ""}{fmtNumber(scenario.bias_pp, 1)} punti percentuali
          {" · "}{scenario.n.toLocaleString("it-IT")} match osservati
        </div>
      </div>
    </div>
  );
}

function PhaseStatus({ name, active, count, description }) {
  const color = active ? NEON : C.muted;
  return (
    <div style={{
      background: C.surface, border: `1px solid ${active ? `${color}33` : C.border}`,
      padding: ".95rem 1rem",
      boxShadow: active ? `0 0 14px ${color}10` : "none",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: ".4rem" }}>
        <span style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: ".82rem",
          color: C.text, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase" }}>
          {name}
        </span>
        {active
          ? <CheckCircle2 size={13} style={{ color, filter: `drop-shadow(0 0 4px ${color})` }} />
          : <Clock size={13} style={{ color }} />}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: ".4rem", marginBottom: ".25rem" }}>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "1.2rem",
          color, fontWeight: 600 }}>
          {active ? count : "—"}
        </span>
        <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: ".68rem", color: C.muted }}>
          {active ? "attivi" : "in attesa dati"}
        </span>
      </div>
      <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: ".7rem",
        color: C.muted, lineHeight: 1.4 }}>
        {description}
      </div>
    </div>
  );
}

/* ─── Main page ───────────────────────────────────────────────────────── */
export default function BrainCrackerPage() {
  const [state, setState] = useState(null);
  const [insightsData, setInsightsData] = useState({ insights: [], deltas: [] });
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchState = useCallback(async () => {
    try {
      // Three reads in parallel — cheap on the backend (single Mongo doc each).
      const [stateRes, insRes, runsRes] = await Promise.all([
        axios.get(`${API}/learning/state`),
        axios.get(`${API}/learning/insights?max_items=8`).catch(() => ({ data: { insights: [], deltas: [] } })),
        axios.get(`${API}/learning/runs?limit=20`).catch(() => ({ data: { runs: [] } })),
      ]);
      setState(stateRes.data);
      setInsightsData({
        insights: insRes.data?.insights || [],
        deltas: insRes.data?.deltas || [],
      });
      setRuns(runsRes.data?.runs || []);
      setLastFetched(new Date());
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + polling 60s + refresh on tab focus, so the page is
  // "always fresh" without forcing the user to reload.
  useEffect(() => { fetchState(); }, [fetchState]);
  useEffect(() => {
    const id = setInterval(fetchState, 60_000);
    const onVisible = () => { if (document.visibilityState === "visible") fetchState(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVisible); };
  }, [fetchState]);

  const insights = useMemo(() => deriveInsights(state), [state]);
  const phaseInfo = useMemo(() => {
    if (!state || !state.applied) return null;
    const m = state.metrics || {};
    const tm = (state.team_markets && state.team_markets.global) || {};
    const halfMarkets = Object.keys(tm).filter((k) =>
      k.startsWith("first_half") || k.startsWith("second_half")).length;
    const teamMultigolMarkets = Object.keys(tm).filter((k) =>
      !(k.startsWith("first_half") || k.startsWith("second_half"))).length;
    return {
      phase1: { active: m.n_markets_calibrated > 0, count: m.n_markets_calibrated },
      phase15: { active: teamMultigolMarkets > 0, count: teamMultigolMarkets },
      phase16: { active: halfMarkets > 0, count: halfMarkets },
      phase2: { active: m.n_pattern_corrections > 0, count: m.n_pattern_corrections },
      phase3: { active: !!(state.dynamic_weights && Object.keys(state.dynamic_weights).length), count: Object.keys(state.dynamic_weights || {}).length },
      phase4: { active: !!m.threshold_backtest_run, count: Object.keys(state.threshold_backtest || {}).length },
    };
  }, [state]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex",
        alignItems: "center", justifyContent: "center" }}>
        <Loader2 size={28} style={{ color: NEON, animation: "spin 1s linear infinite" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (error || !state || !state.applied) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, color: C.text, padding: "5rem 1rem", textAlign: "center" }}>
        <Brain size={48} style={{ color: NEON, opacity: .3, marginBottom: "1rem" }} />
        <h2 style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "1.4rem", letterSpacing: ".2em", margin: 0 }}>
          BRAIN CRACKER
        </h2>
        <p style={{ color: C.muted, marginTop: ".5rem", fontFamily: "'DM Sans',sans-serif" }}>
          {error || "Il cervello non è ancora stato calibrato. Riprova tra qualche minuto."}
        </p>
      </div>
    );
  }

  const m = state.metrics || {};
  const accuracyGain = accuracyBonus(m.logloss_before, m.logloss_after);

  return (
    <div data-testid="brain-cracker-page"
      style={{ minHeight: "100vh", background: C.bg, color: C.text }}>

      {/* ── Hero header ─────────────────────────────────────────────── */}
      <div style={{
        background: `radial-gradient(ellipse at top, ${NEON}0F, transparent 60%)`,
        borderBottom: `1px solid ${C.border}`,
        padding: "2.25rem 1.25rem 1.5rem",
      }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: ".7rem", marginBottom: ".5rem" }}>
            <Brain size={26} style={{ color: NEON, filter: `drop-shadow(0 0 12px ${NEON})` }} />
            <h1 style={{ margin: 0, fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "2rem", letterSpacing: ".22em", textTransform: "uppercase",
              fontWeight: 800 }}>
              Brain <span style={{ color: NEON, textShadow: `0 0 12px ${NEON}` }}>Cracker</span>
            </h1>
            <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: ".4rem",
              fontFamily: "'JetBrains Mono',monospace", fontSize: ".68rem", color: C.muted }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: NEON,
                boxShadow: `0 0 8px ${NEON}` }} />
              LIVE · aggiornato {timeAgo(lastFetched?.toISOString?.())}
              <button onClick={fetchState} title="Refresh manuale"
                style={{ background: "none", border: "none", cursor: "pointer", color: C.muted,
                  padding: 0, display: "flex" }}>
                <RefreshCw size={11} />
              </button>
            </span>
          </div>
          <p style={{ margin: 0, color: C.muted, fontFamily: "'DM Sans',sans-serif",
            fontSize: ".88rem", maxWidth: 720, lineHeight: 1.5 }}>
            Il cervello auto-apprendente di Bet Cracker. Ogni domenica ricalibra le predizioni
            confrontando i suoi pronostici con gli esiti reali, imparando dai propri errori.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "2rem 1.25rem 4rem" }}>

        {/* ── KPI grid ──────────────────────────────────────────────── */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "1rem", marginBottom: "2rem",
        }}>
          <StatCard testid="kpi-version" icon={GitBranch} label="Versione cervello"
            value={`v${state.version || "—"}`} sublabel={`Calibrato ${timeAgo(state.computed_at)}`}
            color={NEON} />
          <StatCard testid="kpi-matches" icon={Database} label="Match analizzati"
            value={(m.n_total_samples || 0).toLocaleString("it-IT")}
            sublabel={`Su ${m.n_leagues || 0} campionati`} color={C.cyan} />
          <StatCard testid="kpi-accuracy" icon={Sparkles} label="Boost accuratezza"
            value={`+${fmtNumber(accuracyGain, 1)}%`}
            sublabel="Vs predizioni grezze" color={NEON} />
          <StatCard testid="kpi-markets" icon={Activity} label="Mercati calibrati"
            value={(m.n_markets_calibrated || 0) + (m.team_markets_count || 0)}
            sublabel={`${m.n_pattern_corrections || 0} pattern di errore`} color={C.amber} />
        </div>

        {/* ── Phase status grid ─────────────────────────────────────── */}
        <div style={{ marginBottom: "2.5rem" }}>
          <SectionTitle icon={Zap} title="Stato fasi auto-apprendimento" />
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: ".75rem",
          }}>
            <PhaseStatus name="Fase 1 · Esiti base" active={phaseInfo?.phase1.active}
              count={phaseInfo?.phase1.count}
              description="Calibra 1X2, Over/Under, Gol/Gol con Platt scaling" />
            <PhaseStatus name="Fase 1.5 · Multigol Squadra" active={phaseInfo?.phase15.active}
              count={phaseInfo?.phase15.count}
              description="Mercati Multigol Casa e Trasferta per band" />
            <PhaseStatus name="Fase 1.6 · Tempi" active={phaseInfo?.phase16.active}
              count={phaseInfo?.phase16.count}
              description="Goal nel 1° e 2° tempo" />
            <PhaseStatus name="Fase 2 · Pattern Errori" active={phaseInfo?.phase2.active}
              count={phaseInfo?.phase2.count}
              description="Correzioni per scenari xG × forza relativa" />
            <PhaseStatus name="Fase 3 · Pesi Dinamici" active={phaseInfo?.phase3.active}
              count={phaseInfo?.phase3.count}
              description="Ribilanciamento Poisson/ELO/Forma per lega" />
            <PhaseStatus name="Fase 4 · Soglie Slot" active={phaseInfo?.phase4.active}
              count={phaseInfo?.phase4.count}
              description="Backtest soglie attivazione Samurai" />
          </div>
        </div>

        {/* ── COSA HO IMPARATO (insights human-readable) ─────────────── */}
        {insightsData.insights.length > 0 && (
          <div style={{ marginBottom: "2.5rem" }} data-testid="insights-panel">
            <SectionTitle icon={Sparkles}
              title="Cosa ho imparato nell'ultima calibrazione"
              subtitle="Correzioni più rilevanti applicate alle predizioni future"
              color={NEON} />
            <div style={{ display: "grid", gap: ".55rem" }}>
              {insightsData.insights.map((it, i) => (
                <InsightRow key={i} insight={it} />
              ))}
            </div>
          </div>
        )}

        {/* ── BARRE Δ PER MERCATO (si ricalibrano a ogni run) ─────────── */}
        {insightsData.deltas.length > 0 && (
          <div style={{ marginBottom: "2.5rem" }} data-testid="market-deltas-panel">
            <SectionTitle icon={Activity}
              title="Correzione media per mercato"
              subtitle="Punti percentuali di differenza fra modello grezzo e modello calibrato"
              color={C.cyan} />
            <div data-testid="market-deltas-bars"
              key={state?.version ?? 'v0'}
              style={{ display: "grid", gap: ".4rem" }}>
              {insightsData.deltas.slice(0, 12).map((d, i) => (
                <DeltaBar key={d.market} delta={d} index={i} />
              ))}
            </div>
            <p style={{ margin: ".75rem 0 0 0", fontSize: ".7rem", color: C.muted,
              fontFamily: "'JetBrains Mono',monospace" }}>
              Verde = mercato sotto-stimato dal grezzo (corretto verso l'alto).
              Ambra = mercato sovra-stimato (corretto verso il basso).
              Le barre si riallineano automaticamente a ogni ricalibrazione.
            </p>
          </div>
        )}

        {/* ── Strengths & Weaknesses ────────────────────────────────── */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))",
          gap: "1rem", marginBottom: "2.5rem",
        }}>
          {/* Strengths */}
          <div data-testid="strengths-panel"
            style={{ background: C.surface, border: `1px solid ${NEON}22`,
              padding: "1.5rem 1.25rem", boxShadow: `0 0 18px ${NEON}10` }}>
            <SectionTitle icon={TrendingUp} title="Su cosa stiamo eccellendo"
              subtitle="Mercati con la maggior crescita di accuratezza dopo calibrazione" color={NEON} />
            {insights.strengths.length === 0 && <EmptyHint />}
            {insights.strengths.map((it, i) => <StrengthRow key={it.key} item={it} rank={i + 1} />)}
          </div>

          {/* Weaknesses */}
          <div data-testid="weaknesses-panel"
            style={{ background: C.surface, border: `1px solid ${C.amber}33`,
              padding: "1.5rem 1.25rem", boxShadow: `0 0 18px ${C.amber}08` }}>
            <SectionTitle icon={AlertCircle} title="Mercati su cui fatichiamo"
              subtitle="Alta imprevedibilità intrinseca anche dopo calibrazione" color={C.amber} />
            {insights.weaknesses.length === 0 && <EmptyHint />}
            {insights.weaknesses.map((it, i) => <WeaknessRow key={it.key} item={it} rank={i + 1} />)}
          </div>
        </div>

        {/* ── Scenario warnings ─────────────────────────────────────── */}
        {insights.scenarios.length > 0 && (
          <div style={{ marginBottom: "2.5rem" }} data-testid="warnings-panel">
            <SectionTitle icon={AlertTriangle} title="Avvisi appresi dai dati storici"
              subtitle="Scenari in cui il modello ha individuato pattern sistematici"
              color={C.amber} />
            <div style={{ display: "grid", gap: ".55rem" }}>
              {insights.scenarios.map((s, i) => <ScenarioWarning key={`${s.market}-${i}`} scenario={s} />)}
            </div>
            <p style={{ margin: ".75rem 0 0 0", fontSize: ".7rem", color: C.muted,
              fontFamily: "'JetBrains Mono',monospace", lineHeight: 1.5 }}>
              Queste correzioni sono già applicate automaticamente alle predizioni —
              le vedi qui per trasparenza.
            </p>
          </div>
        )}

        {/* ── Auto-update note ──────────────────────────────────────── */}
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          padding: "1rem 1.2rem", display: "flex", alignItems: "center", gap: ".75rem",
          borderRadius: 0,
        }}>
          <Clock size={14} style={{ color: NEON, flexShrink: 0 }} />
          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: ".82rem",
            color: C.muted, lineHeight: 1.5 }}>
            Il cervello si ricalibra <strong style={{ color: C.text }}>ogni notte alle 03:00</strong>
            {" "}analizzando le nuove partite chiuse, con un run esteso ogni domenica alle 02:00
            che include anche le fasi più pesanti. Lo stato di questa pagina si aggiorna
            automaticamente ogni 60 secondi.
            {runs.length > 0 && (
              <span style={{ display: "block", marginTop: ".4rem", color: C.muted, fontSize: ".72rem" }}>
                Ultime ricalibrazioni: {runs.slice(0, 5).map((r, i) => (
                  <span key={i} title={`log-loss: ${r.metrics?.logloss_after ?? '—'}`}
                    style={{ display: "inline-block", marginRight: ".5rem",
                      color: r.applied ? NEON : C.amber,
                      fontFamily: "'JetBrains Mono',monospace" }}>
                    {r.applied ? "✓" : "✕"} {String(r.started_at || "").slice(5, 16).replace("T", " ")}
                  </span>
                ))}
              </span>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

function SectionTitle({ icon: Icon, title, subtitle, color = C.text }) {
  return (
    <div style={{ marginBottom: "1rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: ".55rem", marginBottom: ".2rem" }}>
        <Icon size={15} style={{ color, filter: `drop-shadow(0 0 4px ${color}88)` }} />
        <h3 style={{ margin: 0, fontFamily: "'Barlow Condensed',sans-serif",
          fontSize: ".95rem", letterSpacing: ".18em", color: C.text, fontWeight: 700,
          textTransform: "uppercase" }}>
          {title}
        </h3>
      </div>
      {subtitle && (
        <p style={{ margin: 0, fontFamily: "'DM Sans',sans-serif", fontSize: ".75rem",
          color: C.muted, lineHeight: 1.4 }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

function EmptyHint() {
  return (
    <p style={{ color: C.muted, fontFamily: "'JetBrains Mono',monospace",
      fontSize: ".75rem", margin: ".5rem 0" }}>
      Dati insufficienti — ritenta dopo la prossima ricalibrazione.
    </p>
  );
}

/* ─── InsightRow — human-readable bullet for "Cosa ho imparato" ───────── */
function InsightRow({ insight }) {
  const colorMap = {
    critical: "#EF4444",
    warning:  "#F59E0B",
    info:     NEON,
  };
  const tone = colorMap[insight?.severity] || NEON;
  return (
    <details
      data-testid={`insight-row-${insight?.market || "?"}`}
      style={{
        background: C.surface,
        border: `1px solid ${tone}33`,
        padding: ".7rem .9rem",
        boxShadow: `0 0 10px ${tone}10`,
      }}>
      <summary style={{
        cursor: "pointer", listStyle: "none",
        display: "flex", alignItems: "center", gap: ".6rem",
        fontFamily: "'DM Sans',sans-serif", fontSize: ".82rem",
        color: C.text, lineHeight: 1.4,
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: "50%",
          background: tone, boxShadow: `0 0 6px ${tone}`, flexShrink: 0,
        }} />
        <span style={{ flex: 1 }}>{insight?.headline}</span>
        {insight?.n != null && (
          <span style={{
            fontFamily: "'JetBrains Mono',monospace", fontSize: ".68rem",
            color: C.muted, flexShrink: 0,
          }}>
            n={insight.n}
          </span>
        )}
        <ChevronDown size={11} style={{ color: C.muted, flexShrink: 0 }} />
      </summary>
      <p style={{
        margin: ".55rem 0 0 1.1rem", fontFamily: "'DM Sans',sans-serif",
        fontSize: ".78rem", color: C.muted, lineHeight: 1.55,
      }}>
        {insight?.detail}
      </p>
    </details>
  );
}

/* ─── DeltaBar — horizontal bar that fills proportionally to the bias
   correction. ``index`` staggers the fill animation so the bars cascade
   into place every time ``state.version`` changes. */
function DeltaBar({ delta, index = 0 }) {
  const value = Number(delta?.bias_pp || 0);
  const abs = Math.min(Math.abs(value), 20); // cap visualisation at 20pp
  const pct = (abs / 20) * 100;
  // Positive bias (model was under-estimating → corrected UP) → green.
  // Negative bias (model over-estimating → corrected DOWN) → amber.
  const color = value >= 0 ? NEON : "#F59E0B";
  return (
    <div
      data-testid={`delta-bar-${delta?.market || "?"}`}
      style={{
        display: "grid",
        gridTemplateColumns: "160px 1fr 70px",
        alignItems: "center", gap: ".75rem",
        fontFamily: "'JetBrains Mono',monospace",
      }}>
      <span style={{ fontSize: ".74rem", color: C.text, fontWeight: 600,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        title={delta?.label}>
        {delta?.label}
      </span>
      <div style={{
        position: "relative", height: 14,
        background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))",
        border: `1px solid ${C.border}`,
      }}>
        <div
          key={`bar-${delta?.market}-${value}`}
          style={{
            position: "absolute", top: 0, left: 0,
            height: "100%", width: `${pct}%`,
            background: `linear-gradient(90deg, ${color}, ${color}CC)`,
            boxShadow: `0 0 8px ${color}88`,
            animation: `bcDeltaFill 700ms cubic-bezier(.22,.61,.36,1) ${index * 35}ms both`,
          }}
        />
        <style>{`
          @keyframes bcDeltaFill {
            0%   { width: 0%; opacity: 0.3; }
            100% { width: ${pct}%; opacity: 1; }
          }
        `}</style>
      </div>
      <span style={{ fontSize: ".74rem", color, fontWeight: 700,
        textAlign: "right", tabularNums: "auto" }}>
        {value > 0 ? "+" : ""}{value.toFixed(1)}pp
      </span>
    </div>
  );
}
